import * as React from "react";
import {LabelableProps} from "../../common/props/LabelableProps";
import {FormField} from "../../../validation/FormField";
import BaseFormController from "../../../validation/BaseFormController";

/**
 * Base props for every form view.
 */
interface Props {
	/**
	 * Indicates if data is loading.
	 */
	isLoading: boolean;
}

export type BaseFormViewProps = Props & LabelableProps

/**
 * Base state for every form view.
 */
export interface BaseFormViewState<T extends BaseFormController<T>> {
	/**
	 * Form data.
	 */
	form: T
	/**
	 * Indicate if form should be validated.
	 */
	shouldValidate: boolean

	/**
	 * Indicates if form has changed.
	 */
	isFormDirty: boolean
}

/**
 * Every edit form view props.
 */
export interface EditFormViewProps<T> extends BaseFormViewProps {
	/**
	 * Model to populate edit view.
	 */
	model: T
}

export default abstract class BaseFormView<T extends BaseFormController<T>, P, S extends BaseFormViewState<T>>
	extends React.Component<P, S> {
	constructor(props: P) {
		super(props);
		this.onFieldChanged = this.onFieldChanged.bind(this);
		this.onFieldCompleted = this.onFieldCompleted.bind(this);
		this.onFocusChange = this.onFocusChange.bind(this);
		this.onFieldsChanged = this.onFieldsChanged.bind(this);
	}

	/**
	 * Callback onSubmit button click.
	 */
	abstract submitForm(): void

	/**
	 * On change field value.
	 * @param field updated field.
	 */
	onFieldChanged(field: FormField<any>): void {
		const newForm = this.state.form.updateField(field);
		this.setState({form: this.state.shouldValidate ? newForm.validate() : newForm, isFormDirty: true});
	}

	/**
	 * On change fields.
	 * @param fields updated fields.
	 */
	onFieldsChanged(fields: FormField<any> []): void {
		const newForm = this.state.form.updateFields(fields);
		this.setState({form: this.state.shouldValidate ? newForm.validate() : newForm, isFormDirty: true});
	}

	/**
	 * On changes in field has been completed.
	 * @param field updated field.
	 */
	onFieldCompleted(field: FormField<any>): void {
		const newForm = this.state.form.completeField(field);
		this.setState({form: this.state.shouldValidate ? newForm.validate() : newForm, isFormDirty: true});
	}

	/**
	 * On focus has changed.
	 * @param fieldWithFocus new field with focus.
	 */
	onFocusChange(fieldWithFocus: FormField<any>): void {
		this.setState({form: this.state.form.setFocus(fieldWithFocus)});
	}
}